import { MESSAGE_TYPE } from 'shared/constants/messages';
import { applyPageFilters, sortComparator } from './helpers';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import { hasAccessToConversation } from './helpers/permissionHelpers';

export const getSelectedChatConversation = ({
  allConversations,
  selectedChatId,
}) =>
  allConversations.filter(conversation => conversation.id === selectedChatId);

// Module-scoped cache for sorted conversations
// Uses WeakMap keyed by state object to ensure isolation between store instances
// (e.g. in tests or if multiple stores existed)
const sortedConversationsCache = new WeakMap();

const getters = {
  getAllConversations: state => {
    const {
      allConversations,
      chatSortFilter: sortKey,
      conversationsVersion,
    } = state;

    // Use version-based cache key for correctness
    // conversationsVersion is incremented by mutations when conversations change
    const cacheKey = `${sortKey}:${conversationsVersion}`;

    const cached = sortedConversationsCache.get(state);

    if (cached && cached.key === cacheKey && cached.value) {
      return cached.value;
    }

    // Create a copy before sorting to ensure Vue reactivity
    const sorted = [...allConversations].sort((a, b) =>
      sortComparator(a, b, sortKey)
    );

    // Freeze in development to catch accidental mutations
    if (process.env.NODE_ENV !== 'production') {
      Object.freeze(sorted);
    }

    // Update module-scoped cache (not Vuex state) - keeps getter pure
    sortedConversationsCache.set(state, { key: cacheKey, value: sorted });

    return sorted;
  },
  getTeamChats: (_state, _getters, __, rootGetters) => activeFilters => {
    const currentUser = rootGetters.getCurrentUser;
    // Use sorted conversations from getAllConversations getter
    const sortedConversations = _getters.getAllConversations;

    return sortedConversations.filter(conversation => {
      const { team } = conversation.meta;
      const shouldFilter = applyPageFilters(conversation, activeFilters);

      // If there's no team, don't include in team chats
      if (!conversation.meta.team) {
        return false;
      }

      // Check if user is a team member
      const userIsTeamMember = conversation.meta.team.is_member;

      // For control, monitoring, and coordination roles, only show conversations 
      // from teams they are members of
      if (['control', 'monitoring', 'coordination'].includes(currentUser.role)) {
        return shouldFilter && userIsTeamMember;
      }

      // For administrators, show all team conversations
      if (currentUser.role === 'administrator') {
        return shouldFilter;
      }

      // For agents, only show conversations from teams they are members of
      return shouldFilter && userIsTeamMember;
    });
  },
  getSelectedChat: ({ selectedChatId, allConversations }) => {
    const selectedChat = allConversations.find(
      conversation => conversation.id === selectedChatId
    );
    return selectedChat || {};
  },
  getSelectedChatAttachments: (_state, _getters) => {
    const selectedChat = _getters.getSelectedChat;
    return selectedChat.attachments || [];
  },
  getChatListFilters: ({ conversationFilters }) => conversationFilters,
  getLastEmailInSelectedChat: (stage, _getters) => {
    const selectedChat = _getters.getSelectedChat;
    const { messages = [] } = selectedChat;
    const lastEmail = [...messages].reverse().find(message => {
      const {
        content_attributes: contentAttributes = {},
        message_type: messageType,
      } = message;
      const { email = {} } = contentAttributes;
      const isIncomingOrOutgoing =
        messageType === MESSAGE_TYPE.OUTGOING ||
        messageType === MESSAGE_TYPE.INCOMING;
      if (email.from && isIncomingOrOutgoing) {
        return true;
      }
      return false;
    });

    return lastEmail;
  },
  getMineChats: (_state, _getters, __, rootGetters) => activeFilters => {
    const currentUserID = rootGetters.getCurrentUser?.id;
    // Use sorted conversations from getAllConversations getter
    const sortedConversations = _getters.getAllConversations;

    return sortedConversations.filter(conversation => {
      const { assignee } = conversation.meta;
      const isAssignedToMe = assignee && assignee.id === currentUserID;
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      const isChatMine = isAssignedToMe && shouldFilter;

      return isChatMine;
    });
  },
  getAgentBotChats: (_state, _getters) => activeFilters => {
    // Use sorted conversations from getAllConversations getter
    const sortedConversations = _getters.getAllConversations;
    // For bot tab, we want to show all pending conversations regardless of team assignment
    return sortedConversations.filter(conversation => {
      // Apply other filters (inbox, labels, etc.)
      const shouldFilter = applyPageFilters(conversation, activeFilters);

      // Pending means the conversation is still with the bot, even when it already has an assignee
      return shouldFilter && conversation.status === 'pending';
    });
  },
  getAppliedConversationFilters: _state => {
    return _state.appliedFilters;
  },
  getAppliedConversationFiltersQuery: _state => {
    const hasAppliedFilters = _state.appliedFilters.length !== 0;
    return hasAppliedFilters ? filterQueryGenerator(_state.appliedFilters) : [];
  },
  getUnAssignedChats: (_state, _getters, __, rootGetters) => activeFilters => {
    // Use sorted conversations from getAllConversations getter
    const sortedConversations = _getters.getAllConversations;
    return sortedConversations.filter(conversation => {
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      // Strictly match backend definition: no team AND no assignee
      return !conversation.meta.assignee && !conversation.meta.team && shouldFilter;
    });
  },
  getAllStatusChats: (_state, _getters, __, rootGetters) => activeFilters => {
    const currentUser = rootGetters.getCurrentUser;
    const userTeamIds = rootGetters.getUserTeamIds || [];
    // Use sorted conversations from getAllConversations getter
    const sortedConversations = _getters.getAllConversations;

    return sortedConversations.filter(conversation => {
      const { assignee, team } = conversation.meta;
      const shouldFilter = applyPageFilters(conversation, activeFilters);

      // Skip bot conversations
      if (assignee?.bot_id) return false;

      // For administrators show all filtered conversations
      if (currentUser.role === 'administrator') {
        return shouldFilter;
      }

      // For control role - show conversations in their teams or unassigned
      if (['control', 'monitoring', 'coordination'].includes(currentUser.role)) {
        if (!assignee) return shouldFilter;
        if (!team) return shouldFilter;
        return shouldFilter && team.is_member;
      }

      // For agents:
      if (currentUser.role === 'agent') {
        // 1. Show conversations assigned to them
        if (assignee && assignee.id === currentUser.id) return shouldFilter;

        // 2. Show unassigned conversations with no team
        if (!assignee && !team) return shouldFilter;

        // 3. Show unassigned conversations in teams they are members of
        if (!assignee && team && team.is_member) return shouldFilter;

        // 4. Show conversations in teams they lead (even if assigned to others)
        if (team && team.is_member && team.is_leader) return shouldFilter;

        // Hide all other conversations
        return false;
      }

      return shouldFilter;
    });
  },
  getPendingChats: (_state, _getters, __, rootGetters) => activeFilters => {
    const currentUser = rootGetters.getCurrentUser;
    // Use sorted conversations from getAllConversations getter
    const sortedConversations = _getters.getAllConversations;

    return sortedConversations.filter(conversation => {
      const { team } = conversation.meta;
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      const isTeamMember = currentUser.role === 'administrator' || conversation.meta.team?.is_member;
      const isPending = !conversation.meta.assignee && conversation.meta.team;
      return shouldFilter && isTeamMember && isPending;
    });
  },
  getChatListLoadingStatus: ({ listLoadingStatus }) => listLoadingStatus,
  getAllMessagesLoaded(_state) {
    const [chat] = getSelectedChatConversation(_state);
    return !chat || chat.allMessagesLoaded === undefined
      ? false
      : chat.allMessagesLoaded;
  },
  getUnreadCount(_state) {
    const [chat] = getSelectedChatConversation(_state);
    if (!chat) return [];
    return chat.messages.filter(
      chatMessage =>
        chatMessage.created_at * 1000 > chat.agent_last_seen_at * 1000 &&
        chatMessage.message_type === 0 &&
        chatMessage.private !== true
    ).length;
  },
  getChatStatusFilter: ({ chatStatusFilter }) => chatStatusFilter,
  getChatSortFilter: ({ chatSortFilter }) => chatSortFilter,
  getSelectedInbox: ({ currentInbox }) => currentInbox,
  getIsFetchingConversation: ({ isFetchingConversation }) => isFetchingConversation,
  getConversationById: _state => conversationId => {
    return _state.allConversations.find(
      value => value.id === Number(conversationId)
    );
  },
  getConversationParticipants: _state => {
    return _state.conversationParticipants;
  },
  getConversationLastSeen: _state => {
    return _state.conversationLastSeen;
  },

  getContextMenuChatId: _state => {
    return _state.contextMenuChatId;
  },
  getTakeOverConversationsIds: _state => {
    return _state.takeOverConversationIds;
  }
};

export default getters;